/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { translate } from '@utils/i18n';
import { TosRequiredError } from '../services/orchestrator-api-client';
import { DayBriefStartError } from '../services/dayBriefChannel';
import { buildAgentMessage, DEFAULT_AGENT } from '../helpers';
import { DAY_BRIEF_DEFAULT_TEXT, DAY_BRIEF_ERROR_MESSAGE_ID, DAY_BRIEF_FAILURE_REASON, DAY_BRIEF_TRANSLATION_NAMESPACE, FIRST_RUN_CONVERSATION_DELETED } from '../constants/dayBrief';
const HTTP_FORBIDDEN = 403;
const HTTP_SERVER_ERROR_FLOOR = 500;
// Statuses a later attempt can plausibly clear: expired token, timeout, rate limit.
const RETRYABLE_CLIENT_STATUSES = new Set([401, 408, 429]);
function responseOf(err) {
    return err === null || err === void 0 ? void 0 : err.response;
}
// The orchestrator emits either `{ error }` or FastAPI's `{ detail: { error } }`.
function errorCodeOf(err) {
    var _a, _b;
    const data = (_a = responseOf(err)) === null || _a === void 0 ? void 0 : _a.data;
    const detail = data === null || data === void 0 ? void 0 : data.detail;
    return (_b = data === null || data === void 0 ? void 0 : data.error) !== null && _b !== void 0 ? _b : (typeof detail === 'object' ? detail === null || detail === void 0 ? void 0 : detail.error : detail);
}
export function isFirstRunConversationDeleted(err) {
    var _a;
    return ((_a = responseOf(err)) === null || _a === void 0 ? void 0 : _a.status) === 409 && errorCodeOf(err) === FIRST_RUN_CONVERSATION_DELETED;
}
/**
 * Maps a failed `POST /chat/first-run` to what the host is told. No response at
 * all (network drop, timeout) is retryable: the request may never have arrived,
 * and the route is idempotent on `client_request_id`.
 */
export function toDayBriefError(err) {
    var _a;
    if (err instanceof DayBriefStartError)
        return err;
    if (err instanceof TosRequiredError)
        return new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.TERMS_REQUIRED, true);
    const status = (_a = responseOf(err)) === null || _a === void 0 ? void 0 : _a.status;
    if (status === undefined || status >= HTTP_SERVER_ERROR_FLOOR || RETRYABLE_CLIENT_STATUSES.has(status)) {
        return new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.TEMPORARILY_UNAVAILABLE, true);
    }
    if (status === HTTP_FORBIDDEN)
        return new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.NOT_ELIGIBLE, false);
    return new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.REQUEST_REJECTED, false);
}
/**
 * A day brief can run once the AI token is minted and the terms are accepted.
 * Waiting is open-ended while the TOS modal is up (the technician has to act);
 * everywhere else a settled failure becomes a retryable error rather than a
 * command that never answers.
 */
export function resolveDayBriefReadiness({ aiToken, aiTokenError, tosGate, isTosStatusLoading }) {
    const unavailable = () => ({
        status: 'failed',
        error: new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.TEMPORARILY_UNAVAILABLE, true)
    });
    if (tosGate === 'blocked-awaiting-acceptance')
        return { status: 'waiting' };
    if (!aiToken)
        return aiTokenError ? unavailable() : { status: 'waiting' };
    if (tosGate === 'blocked-status-unknown')
        return isTosStatusLoading ? { status: 'waiting' } : unavailable();
    return { status: 'ready' };
}
// `translate` returns the bare key when a host supplies neither window.I18n nor an
// override for it, so fall back to English rather than render the key.
export function dayBriefText(key) {
    const text = translate(key, { namespace: DAY_BRIEF_TRANSLATION_NAMESPACE });
    return text === `${DAY_BRIEF_TRANSLATION_NAMESPACE}.${key}` ? DAY_BRIEF_DEFAULT_TEXT[key] : text;
}
export function buildDayBriefErrorMessage(error) {
    return buildAgentMessage({
        id: DAY_BRIEF_ERROR_MESSAGE_ID,
        responseId: DAY_BRIEF_ERROR_MESSAGE_ID,
        conversationId: '',
        agent: DEFAULT_AGENT,
        blocks: [
            {
                type: 'markdown',
                block_id: `${DAY_BRIEF_ERROR_MESSAGE_ID}-0`,
                block_index: 0,
                content: dayBriefText(error.retryable ? 'error_retryable' : 'error_unavailable')
            }
        ]
    });
}
